import React, { useCallback, useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  MenuItem,
  DialogActions,
  Icon,
  Grid,
  CircularProgress,
} from '@material-ui/core'
import Formsy from 'formsy-react'
import { SelectFormsy, FuseAnimateGroup } from '@fuse'
import NotificationPreview from './NotificationPreview'
import {
  startFullScreenLoading,
  endFullScreenLoading,
} from 'app/store/actions/loadSpinner.actions'
import { useDispatch } from 'react-redux'
import { useIntl, FormattedMessage } from 'react-intl'
import { useFetcher } from 'libs/fetcher'
import effects from 'app/effects/effects'
import Loader from 'app/components/Loader'
import PropTypes from 'prop-types'
import NotificationPropTypes from './NotificationPropTypes'
import * as R from 'ramda'
import {
  equals,
  mergeRight,
  propEq,
  when,
  ifElse,
  pick,
  compose,
  filter,
  map,
  prop,
  trim,
  addIndex,
} from 'ramda'
import {
  getTopicIdsQueryParams,
  getChannelsQueryParams,
} from './TranslationParser'
import { makeStyles } from '@material-ui/styles'
import ErrorBoundary from 'app/components/ErrorBoundary'
import ErrorView from 'app/components/ErrorView'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import { useBreakpoints } from 'commons/useBreakpoints'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import { LanguageSubscriberCount } from 'app/components/selectOptional'
import { useLanguagePreview } from 'commons/useLanguagePreview'
import { isEmptyOrNil } from 'libs/empty'
import { TYPE_POLL } from 'app/constants/messageType'

// Helper functions for poll translation
export const isPollNotification = propEq('type', TYPE_POLL)

export const extractPollOptionTitles = (options) => {
  if (!Array.isArray(options)) return []
  return compose(
    map(compose(trim, prop('title'))),
    filter((opt) => {
      const title = prop('title', opt)
      return title && trim(title) !== ''
    }),
    filter(Boolean),
  )(options)
}

export const mergeTranslatedOptions = (translatedTitles, originalOptions) => {
  if (
    !Array.isArray(translatedTitles) ||
    !Array.isArray(originalOptions) ||
    originalOptions.length === 0
  ) {
    return undefined
  }

  const originalOptionsWithTitles = filter((opt) => {
    const title = prop('title', opt)
    return title && trim(title) !== ''
  }, originalOptions)

  // Use addIndex to get index parameter in map
  const mapWithIndex = addIndex(map)
  return mapWithIndex((translatedTitle, index) => {
    const originalOption = originalOptionsWithTitles[index]
    return {
      title:
        translatedTitle ||
        (originalOption ? prop('title', originalOption) : '') ||
        '',
      imagePath: originalOption
        ? prop('imagePath', originalOption) || null
        : null,
    }
  }, translatedTitles)
}

export const buildPollTranslateData = (notification) => {
  const base = { languageId: notification.languageId }
  return compose(
    when(
      () => Array.isArray(prop('options', notification)),
      mergeRight({ options: extractPollOptionTitles(notification.options) }),
    ),
    when(
      () => prop('question', notification),
      mergeRight({ question: notification.question }),
    ),
  )(base)
}

export const buildGeneralTranslateData = (notification) =>
  pick(
    ['languageId', 'title', 'body', 'voucherTitle', 'voucherDescription'],
    notification,
  )

const useStyles = makeStyles((theme) => ({
  classClose: {
    textAlign: 'center',
    fontSize: '20px',
    cursor: 'pointer',
  },
  iconSize: {
    fontSize: '22px',
    color: '#000000',
    fontWeight: 'bold',
  },
  dialogPaper: {
    [useBreakpoints(theme).lessThan.sm]: {
      height: '100%',
      maxHeight: '100%',
      margin: '0px',
    },
  },
}))

const TranslationPreview = ({
  notification,
  open,
  onClose,
  onClickSendNow,
  recipients,
  topics,
  channels,
  subscriberCount = 0,
  isInitialNotification = false,
  submitLabelId,
  isSubmitButtonDisabled,
  pages,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const defaultLanguage = useDefaultLanguage()
  const [isTranslateLoading, setIsTranslateLoading] = useState(false)
  const [translateError, setTranslateError] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage.id)
  const [shownLanguages, setShownLanguages] = useState([])
  const [detectedLanguage, setDetectedLanguage] = useState(null)
  const [translatedNotification, setTranslatedNotification] = useState({
    ...notification,
  })

  const [languageBySubGroups] = useState({
    subgroups: R.isNil(recipients) ? [] : R.join(',', recipients.subGroups),
    subscribers: R.isNil(recipients)
      ? []
      : R.join(
          ',',
          recipients.subscribers.map((item) => item.id),
        ),
    topicIds: getTopicIdsQueryParams(topics),
    channels: getChannelsQueryParams(channels),
  })

  const apiFetchersCallback = useCallback(
    () =>
      effects.apis.getLanguageBySubGroups({
        subgroups: languageBySubGroups.subgroups,
        subscribers: languageBySubGroups.subscribers,
        topicIds: languageBySubGroups.topicIds,
        channels: languageBySubGroups.channels,
        isAllPlaceLanguage: isEmptyOrNil(languageBySubGroups.channels),
      }),
    [languageBySubGroups],
  )

  const { values: subscriberLanguage, isLoading: isLanguageLoading } =
    useFetcher(apiFetchersCallback, [])
  const { detectLanguage, sortLanguage } = useLanguagePreview(
    notification,
    subscriberLanguage,
    defaultLanguage,
  )

  const translate = (language) => {
    const detectedLanguage = equals(language, detectLanguage.id)
    const isPoll = isPollNotification(notification)

    setTranslateError(null)

    if (detectedLanguage) {
      // Use original content if same as detected language
      const originalContent = isPoll
        ? pick(['question', 'options'], notification)
        : pick(
            ['title', 'body', 'voucherTitle', 'voucherDescription'],
            notification,
          )
      translateNotification(originalContent)
      return
    }

    setIsTranslateLoading(true)

    const translateData = isPoll
      ? buildPollTranslateData({ ...notification, languageId: language })
      : buildGeneralTranslateData({ ...notification, languageId: language })

    effects.apis
      .translate({ data: translateData })
      .then((result) => {
        setIsTranslateLoading(false)

        const translated = isPoll
          ? {
              question: result.question,
              options: mergeTranslatedOptions(
                result.options,
                notification.options,
              ),
            }
          : pick(
              ['title', 'body', 'voucherTitle', 'voucherDescription'],
              result,
            )

        translateNotification(translated)
      })
      .catch((error) => {
        setIsTranslateLoading(false)
        setTranslateError(error)
      })
  }

  const translateNotification = (translatedContent) => {
    const isPoll = isPollNotification(notification)
    const fieldsToPick = isPoll
      ? ['question', 'options']
      : ['title', 'body', 'voucherTitle', 'voucherDescription']

    const updateData = pick(fieldsToPick, translatedContent)
    const mergedNotification = mergeRight(notification, updateData)
    setTranslatedNotification(mergedNotification)
  }

  useEffect(() => {
    dispatch(
      isLanguageLoading ? startFullScreenLoading() : endFullScreenLoading(),
    )
  }, [dispatch, isLanguageLoading])

  useEffect(() => {
    if (!sortLanguage) {
      return
    }
    const isSocialMedia = !R.isEmpty(pages) && R.isEmpty(channels)
    const shownLanguages = isSocialMedia ? [detectLanguage] : sortLanguage
    setShownLanguages(shownLanguages)
    setDetectedLanguage(detectLanguage.id)
  }, [channels, pages, detectLanguage, sortLanguage])

  if (isLanguageLoading) return null

  return (
    <div>
      <Dialog
        open={open}
        onClose={onClose}
        disableBackdropClick={true}
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <DialogTitle className="pb-4 mt-8">
          <Grid container spacing={2}>
            <Grid item xs={11}>
              <FormattedMessage
                id={
                  isInitialNotification
                    ? 'translationPreview.initialNoti.title'
                    : 'translationPreview.title'
                }
              />
            </Grid>
            <Grid item xs={1} className={classes.classClose} onClick={onClose}>
              <Icon className={classes.iconSize} color="action">
                close
              </Icon>
            </Grid>
          </Grid>
        </DialogTitle>
        <ErrorBoundary fallback={<ErrorView />}>
          <Loader
            isLoading={isLanguageLoading}
            placeholder={
              <FuseAnimateGroup
                enter={{ animation: 'transition.fadeIn' }}
                leave={{ animation: 'transition.fadeOut' }}
              >
                <div className="flex justify-center py-64">
                  <CircularProgress color="secondary" />
                </div>
              </FuseAnimateGroup>
            }
          >
            {() => (
              <>
                <DialogContent className="pt-4 w-full sm:min-w-sm">
                  {isInitialNotification ? null : (
                    <>
                      <DialogContentText className="md:px-8">
                        <b>
                          {R.isNil(subscriberCount) ||
                          isInitialNotification ? null : (
                            <FormattedMessage
                              id="translationPreview.subtitle1"
                              values={{ subscriberCount: subscriberCount }}
                            />
                          )}
                        </b>
                        <br />
                        {isInitialNotification ? null : (
                          <FormattedMessage id="translationPreview.subtitle3" />
                        )}
                      </DialogContentText>

                      <Formsy>
                        <SelectFormsy
                          className="w-full mt-16"
                          name="translation"
                          label={formatMessage({
                            id: 'translationPreview.translationSelectLabel',
                          })}
                          value={detectedLanguage || defaultLanguage.id}
                          onChange={(event) => {
                            setSelectedLanguage(event.target.value)
                            translate(event.target.value)
                          }}
                          variant="outlined"
                        >
                          {shownLanguages.map((language) => (
                            <MenuItem key={language.id} value={language.id}>
                              <LanguageSubscriberCount
                                name={language.name}
                                nativeName={language.nativeName}
                                count={language.count}
                              />
                            </MenuItem>
                          ))}
                        </SelectFormsy>
                      </Formsy>
                    </>
                  )}
                  <div className="w-full flex justify-center py-10">
                    <Loader
                      isLoading={isTranslateLoading}
                      placeholder={
                        <FuseAnimateGroup
                          enter={{ animation: 'fadeIn' }}
                          leave={{ animation: 'fadeOut' }}
                        >
                          <div className="flex py-64">
                            <CircularProgress color="secondary" />
                          </div>
                        </FuseAnimateGroup>
                      }
                    >
                      {R.isNil(translateError) ? (
                        <NotificationPreview
                          notification={translatedNotification}
                          isInitialNotification={isInitialNotification}
                        />
                      ) : (
                        <FuseAnimateGroup
                          enter={{ animation: 'transition.expandIn' }}
                          leave={{ animation: 'transition.expandOut' }}
                        >
                          <>
                            <ErrorView
                              subtitle={translateError.message}
                              action={() => translate(selectedLanguage)}
                            />
                          </>
                        </FuseAnimateGroup>
                      )}
                    </Loader>
                  </div>
                </DialogContent>
                <DialogActions>
                  <div className="w-full flex justify-between sm:justify-end pb-8 px-16">
                    <SecondaryButton
                      className="flex self-center w-1/2 sm:w-auto sm:ltr:ml-16 rtl:mr-16 ltr:mr-4 rtl:ml-4 sm:ltr:mr-8 rtl:ml-8"
                      onClick={onClose}
                    >
                      <FormattedMessage id="translationPreview.backButton" />
                    </SecondaryButton>
                    <PrimaryButton
                      className="flex self-center w-1/2 sm:w-auto ltr:ml-4 rtl:mr-4 sm:ltr:ml-8 rtl:mr-8"
                      onClick={onClickSendNow}
                      disabled={isSubmitButtonDisabled}
                    >
                      {
                        <FormattedMessage
                          id={
                            isInitialNotification
                              ? 'editInitialNotification.dialog.saveButton.label'
                              : submitLabelId
                          }
                        />
                      }
                    </PrimaryButton>
                  </div>
                </DialogActions>
              </>
            )}
          </Loader>
        </ErrorBoundary>
      </Dialog>
    </div>
  )
}

TranslationPreview.propTypes = {
  notification: PropTypes.shape(NotificationPropTypes), // notification object
  open: PropTypes.bool.isRequired, // open dialog state
  onClose: PropTypes.func, // set open dialog state callback
  isInitialNotification: PropTypes.bool, // set to show initial notification preview
}

export default TranslationPreview
